import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import countries from 'shared/constants/countries.js';

/**
 * Countries to pick from in the rows of a marketplace inbox's settings (signatures and bots by
 * country), keyed by the lowercase ISO 3166 alpha-2 code the inbox stores. Named in the
 * dashboard's language ("Německo (DE)"), English as the fallback, and sorted by that name.
 */
export function useCountryOptions() {
  const { locale } = useI18n();

  // Locales such as pt_BR need a dash for Intl.
  const intlLocale = computed(() => locale.value.replace('_', '-'));

  const regionNames = computed(() => {
    try {
      return new Intl.DisplayNames([intlLocale.value], { type: 'region' });
    } catch (e) {
      return null;
    }
  });

  const compareNames = (a, b) => {
    try {
      return a.localeCompare(b, intlLocale.value);
    } catch (e) {
      return a.localeCompare(b);
    }
  };

  const countryOptions = computed(() =>
    countries
      .map(({ id, name }) => ({
        value: id.toLowerCase(),
        name: regionNames.value?.of(id) || name,
        code: id,
      }))
      .sort((a, b) => compareNames(a.name, b.name))
      .map(({ value, name, code }) => ({ value, label: `${name} (${code})` }))
  );

  // A country can be in one row only, so the countries of the other rows are not offered.
  const countryOptionsFor = (rows, row) => {
    const taken = new Set(
      rows
        .filter(other => other !== row && other.country)
        .map(other => other.country)
    );
    return countryOptions.value.filter(option => !taken.has(option.value));
  };

  return { countryOptions, countryOptionsFor };
}
